/**
 * @fileoverview Root application providers + startup wiring.
 * @module app/providers
 * @description
 * Composes the app-wide providers (outside-in: QueryClient -> i18n -> ErrorBoundary)
 * and performs the startup wiring that Angular ran across `app.config.ts`, the
 * AppComponent constructor, and the APP_INITIALIZER:
 *   - theme init, language init, network-status init, offline-queue init, tooltip init
 *   - version check on startup
 *   - ConsoleLogger construction (side-effect of importing the singleton)
 *
 * Stores exposing `destroy()` are torn down in the effect cleanup.
 *
 * @author Liraz Amir
 */

import { useEffect, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';

import { queryClient } from './query-client';
import { i18n } from '@/i18n';
import { ErrorBoundary } from '@/components/common/error-boundary/ErrorBoundary';
import { TooltipHost } from '@/components/common/tooltip/TooltipHost';
import { ToastContainer } from '@/components/common/toast/ToastContainer';

// Importing the console-logger singleton runs its constructor side-effect
// (console interception), mirroring Angular providing ConsoleLogger at startup.
import { consoleLogger } from '@/services/console/console-logger';

import { useThemeStore } from '@/stores/theme.store';
import { useLanguageStore } from '@/stores/language.store';
import { useNetworkStatusStore } from '@/stores/network-status.store';
import { useOfflineQueueStore } from '@/stores/offline-queue.store';
import { useTooltipStore } from '@/stores/tooltip.store';
import { useVersionStore } from '@/stores/version.store';
import { useSessionTimeoutStore } from '@/stores/session-timeout.store';
import { useLocalAuthStore } from '@/stores/local-auth.store';
import { useToastStore } from '@/stores/toast.store';
import { router } from '@/app/router';

import { SessionTimeoutWarning } from '@/components/security/session-timeout-warning/SessionTimeoutWarning';

// Reference the logger so the import is not tree-shaken / flagged as unused.
void consoleLogger;

interface ProvidersProps {
  children: ReactNode;
}

/** Top-level providers + one-time startup wiring (Angular APP_INITIALIZER equivalent). */
export function Providers({ children }: ProvidersProps) {
  useEffect(() => {
    // Wire i18next into the language store's translator so setLanguage/initialize
    // drive react-i18next's changeLanguage (the store uses an adapter contract).
    useLanguageStore.getState().setTranslator({
      use: (lng: string) => {
        void i18n.changeLanguage(lng);
      },
      getBrowserLang: () =>
        typeof navigator !== 'undefined' && navigator.language
          ? navigator.language.split('-')[0]
          : undefined,
    });

    // Startup wiring (mirrors Angular app.config / AppComponent constructor / APP_INITIALIZER).
    useThemeStore.getState().initTheme();
    useLanguageStore.getState().initialize();
    useNetworkStatusStore.getState().init();
    useOfflineQueueStore.getState().init();
    useTooltipStore.getState().init();
    // APP_INITIALIZER ran the version check on startup.
    void useVersionStore.getState().checkForUpdate();

    // Session-timeout lifecycle (mirrors Angular):
    // - start/stopMonitoring() are driven by the local-auth store on
    //   signIn/signOut. If a session is already active on startup, resume
    //   monitoring so a reloaded authenticated tab keeps the inactivity timer.
    // - The Angular signOut navigated to /login after clearing the session; the
    //   React local-auth store leaves navigation to callers, so we register a
    //   logout handler that runs signOut() then navigates to /login (using the
    //   router instance, since this provider sits above the RouterProvider).
    useSessionTimeoutStore.getState().setLogoutHandler(async () => {
      await useLocalAuthStore.getState().signOut();
      await router.navigate('/login');
      useToastStore
        .getState()
        .showToast('Your session has expired due to inactivity. Please log in again.', 'warning');
    });
    void useLocalAuthStore
      .getState()
      .getSession()
      .then((session) => {
        if (session) {
          useSessionTimeoutStore.getState().startMonitoring();
        }
      })
      .catch(() => {
        // Non-fatal: no active session to resume monitoring for.
      });

    return () => {
      // Tear down stores that expose cleanup (idempotent inits guard re-entry).
      // (offline-queue does not expose destroy(); its interval is process-lived.)
      useTooltipStore.getState().destroy();
      useNetworkStatusStore.getState().destroy();
      useSessionTimeoutStore.getState().stopMonitoring();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <ErrorBoundary>
          {children}
          <ToastContainer />
          <TooltipHost />
          {/* Global session-timeout warning modal (Angular mounted this in app.html). */}
          <SessionTimeoutWarning />
        </ErrorBoundary>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default Providers;
